import { useState } from "react";
import type { FormEvent } from "react";
import { login } from "../lib/api";
import type { AuthState } from "../lib/types";
import { FormField } from "../components/admin-ui";

interface LoginPageProps {
  navigate: (path: string) => void;
  onLogin: (auth: AuthState) => void;
}

function destinationFor(auth: AuthState): string {
  if (auth.user.globalRole === "owner") return "/owner";
  if (auth.memberships.some((membership) => membership.role === "owner" || membership.role === "manager")) return "/app";
  if (auth.memberships.some((membership) => membership.role === "door_staff")) return "/scanner";
  return "/events";
}

export default function LoginPage({ navigate, onLogin }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const auth = await login(email, password);
      onLogin(auth);
      navigate(destinationFor(auth));
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Login failed. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="page auth-page">
      <form className="auth-panel auth-panel--login" onSubmit={handleSubmit}>
        <img className="auth-logo" src="/antix-logo.png" alt="AnTix logo" />
        <span className="eyebrow">Organizer and staff access</span>
        <h1>Sign in to AnTix</h1>
        <p>Use your owner, promoter, or door staff account to enter the AnTix workspace.</p>
        <FormField label="Email">
          <input
            autoComplete="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>
        <FormField label="Password">
          <input
            autoComplete="current-password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>
        {error && <p className="form-error">{error}</p>}
        <button type="submit" className="primary-action" disabled={isSubmitting}>
          {isSubmitting ? "Signing in..." : "Login"}
        </button>
      </form>
    </section>
  );
}
